import { NacelleConnector } from '../connectors/nacelleConnector'
import {
  FetchProductParams,
  FetchProductsParams,
  FetchCollectionParams,
  FetchCollectionPageParams,
  FetchContentParams,
  FetchPageParams,
  FetchPagesParams,
  FetchArticleParams,
  FetchArticlesParams,
  FetchBlogParams,
  FetchBlogPageParams
} from '../connectors/interfaces/parameters'
import { Product } from '../objects/product'
import { Collection } from '../objects/collection'
import { Content } from '../objects/content'
import { Space } from '../objects/space'

export interface NacelleDataParams {
  connector: NacelleConnector
  locale?: string
  currencyCode?: string
  onDataError?: <T>(options: OnDataErrorParams) => Promise<T> | T
}

export interface NacelleDataUpdateParams {
  connector?: NacelleConnector
  locale?: string
  currencyCode?: string
  onDataError?: <T>(options: OnDataErrorParams) => Promise<T> | T
}

interface AfterSubscription {
  method: string
  callback: <T>(responseObj: T) => Promise<T> | T
}

interface OnDataErrorParams {
  method: string
  params: any
  error: Error
}

interface OnDataError {
  <T>(options: OnDataErrorParams): Promise<T> | T
}

export default class NacelleData {
  connector: NacelleConnector
  locale: string
  currencyCode: string
  afterSubscriptions: AfterSubscription[]
  onDataError: OnDataError | null

  constructor(params: NacelleDataParams) {
    this.connector = params.connector
    this.locale = params.locale || 'en-us'
    this.currencyCode = params.currencyCode || 'USD'
    this.afterSubscriptions = []
    this.onDataError = null

    if (params.onDataError) {
      this.onDataError = params.onDataError
    }

    this.connector.updateDefaults({
      locale: this.locale
    })
  }

  update(params: NacelleDataUpdateParams) {
    if (params.connector) {
      this.connector = params.connector
    }

    if (params.locale) {
      this.locale = params.locale

      this.connector.updateDefaults({
        locale: this.locale
      })
    }

    if (params.currencyCode) {
      this.currencyCode = params.currencyCode
    }

    if (typeof params.onDataError !== 'undefined') {
      this.onDataError = params.onDataError
    }
  }

  async product(
    params: FetchProductParams,
    connector?: NacelleConnector
  ): Promise<Product> {
    try {
      let product: Product

      if (connector) {
        product = await connector.product(params)
      } else {
        product = await this.connector.product(params)
      }

      return this.applyAfter<Product>('product', product)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Product>({
          method: 'product',
          params,
          error
        })
      }

      throw error
    }
  }

  async products(
    params: FetchProductsParams,
    connector?: NacelleConnector
  ): Promise<Product[]> {
    try {
      let products: Product[]

      if (connector) {
        products = await connector.products(params)
      } else {
        products = await this.connector.products(params)
      }

      return this.applyAfter<Product[]>('products', products)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Product[]>({
          method: 'products',
          params,
          error
        })
      }

      throw error
    }
  }

  async collection(
    params: FetchCollectionParams,
    connector?: NacelleConnector
  ): Promise<Collection> {
    try {
      let collection: Collection

      if (connector) {
        collection = await connector.collection(params)
      } else {
        collection = await this.connector.collection(params)
      }

      return this.applyAfter<Collection>('collection', collection)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Collection>({
          method: 'collection',
          params,
          error
        })
      }

      throw error
    }
  }

  async collectionPage(
    params: FetchCollectionPageParams,
    connector?: NacelleConnector
  ): Promise<Product[]> {
    try {
      let products: Product[]
      if (connector) {
        products = await connector.collectionPage(params)
      } else {
        products = await this.connector.collectionPage(params)
      }

      return this.applyAfter<Product[]>('collectionPage', products)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Product[]>({
          method: 'collectionPage',
          params,
          error
        })
      }

      throw error
    }
  }

  async content(
    params: FetchContentParams,
    connector?: NacelleConnector
  ): Promise<Content> {
    try {
      let content: Content

      if (connector) {
        content = await connector.content(params)
      } else {
        content = await this.connector.content(params)
      }

      return this.applyAfter<Content>('content', content)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Content>({
          method: 'content',
          params,
          error
        })
      }

      throw error
    }
  }

  async page(
    params: FetchPageParams,
    connector?: NacelleConnector
  ): Promise<Content> {
    try {
      let page: Content

      if (connector) {
        page = await connector.page(params)
      } else {
        page = await this.connector.page(params)
      }

      return this.applyAfter<Content>('page', page)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Content>({
          method: 'page',
          params,
          error
        })
      }

      throw error
    }
  }

  async pages(
    params: FetchPagesParams,
    connector?: NacelleConnector
  ): Promise<Content[]> {
    try {
      let pages: Content[]

      if (connector) {
        pages = await connector.pages(params)
      } else {
        pages = await this.connector.pages(params)
      }

      return this.applyAfter<Content[]>('pages', pages)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Content[]>({
          method: 'pages',
          params,
          error
        })
      }

      throw error
    }
  }

  async article(
    params: FetchArticleParams,
    connector?: NacelleConnector
  ): Promise<Content> {
    try {
      let article: Content

      if (connector) {
        article = await connector.article(params)
      } else {
        article = await this.connector.article(params)
      }

      return this.applyAfter<Content>('article', article)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Content>({
          method: 'article',
          params,
          error
        })
      }

      throw error
    }
  }

  async articles(
    params: FetchArticlesParams,
    connector?: NacelleConnector
  ): Promise<Content[]> {
    try {
      let articles: Content[]

      if (connector) {
        articles = await connector.articles(params)
      } else {
        articles = await this.connector.articles(params)
      }

      return this.applyAfter<Content[]>('articles', articles)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Content[]>({
          method: 'articles',
          params,
          error
        })
      }

      throw error
    }
  }

  async blog(
    params: FetchBlogParams,
    connector?: NacelleConnector
  ): Promise<Content> {
    try {
      let blog: Content

      if (connector) {
        blog = await connector.blog(params)
      } else {
        blog = await this.connector.blog(params)
      }

      return this.applyAfter<Content>('blog', blog)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Content>({
          method: 'blog',
          params,
          error
        })
      }

      throw error
    }
  }

  async blogPage(
    params: FetchBlogPageParams,
    connector?: NacelleConnector
  ): Promise<Content[]> {
    try {
      let articles: Content[]

      if (connector) {
        articles = await connector.blogPage(params)
      } else {
        articles = await this.connector.blogPage(params)
      }

      return this.applyAfter<Content[]>('blogPage', articles)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Content[]>({
          method: 'blogPage',
          params,
          error
        })
      }

      throw error
    }
  }

  async space(connector?: NacelleConnector): Promise<Space> {
    try {
      let space: Space

      if (connector) {
        space = await connector.space()
      } else {
        space = await this.connector.space()
      }

      return this.applyAfter<Space>('space', space)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Space>({
          method: 'space',
          params: {},
          error
        })
      }

      throw error
    }
  }

  async allProducts(
    params?: { limit?: number },
    connector?: NacelleConnector
  ): Promise<Product[]> {
    try {
      let products: Product[]

      if (connector) {
        products = await connector.allProducts(params)
      } else {
        products = await this.connector.allProducts(params)
      }

      return this.applyAfter<Product[]>('allProducts', products)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Product[]>({
          method: 'allProducts',
          params,
          error
        })
      }

      throw error
    }
  }

  async allCollections(
    params?: { limit?: number },
    connector?: NacelleConnector
  ): Promise<Collection[]> {
    try {
      let collections: Collection[]

      if (connector) {
        collections = await connector.allCollections(params)
      } else {
        collections = await this.connector.allCollections(params)
      }

      return this.applyAfter<Collection[]>('allCollections', collections)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Collection[]>({
          method: 'allCollections',
          params,
          error
        })
      }

      throw error
    }
  }

  async allContent(
    params?: { limit?: number },
    connector?: NacelleConnector
  ): Promise<Content[]> {
    try {
      let content: Content[]

      if (connector) {
        content = await connector.allContent(params)
      } else {
        return this.connector.allContent(params)
      }

      return this.applyAfter<Content[]>('allContent', content)
    } catch (error) {
      if (this.onDataError) {
        return this.onDataError<Content[]>({
          method: 'allContent',
          params,
          error
        })
      }

      throw error
    }
  }

  after(method: string, callback: <T>(responseObj: T) => Promise<T> | T): void {
    this.afterSubscriptions.push({
      method,
      callback
    })
  }

  private async applyAfter<T>(method: string, responseObj: T): Promise<T> {
    let response = responseObj
    const subs = this.afterSubscriptions.filter(sub => sub.method === method)

    for (const sub of subs) {
      response = await sub.callback<T>(response)
    }

    return response
  }
}
